import React from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Typography, Box, Divider } from "@mui/material";
import {
  CompanyConfigSettingsSchemaInterface,
  CompanyMandatoryFieldsNames,
} from "Models/company.config";
import { MandatoryFieldDisplay, MandatoryFieldTitle } from "./styled";
import { Checkbox } from "Components/Form";

interface ItemCategoryConfigDetailsProps {
  /**
   * Whether the item category configuration is enabled
   */
  isEnabled: boolean;

  /**
   * Object containing category activation status
   * Keys are category identifiers and values are boolean activation states
   */
  categories: Record<CompanyMandatoryFieldsNames, boolean>;

  /**
   * Object containing mandatory fields configuration for each category
   */
  mandatoryFields: {
    M_E: {
      Make: boolean;
      Model: boolean;
      Serial: boolean;
      Checklist: boolean;
      Comments: boolean;
      Photos: boolean;
      [key: string]: boolean;
    };
    Pro_gear: {
      Comments: boolean;
      Photos: boolean;
      Checklist: boolean;
      Weight: boolean;
      Volume: boolean;
      WeightOrVolume: boolean;
      [key: string]: boolean;
    };
    High_risk_High_value: {
      Make: boolean;
      Model: boolean;
      Serial: boolean;
      Comments: boolean;
      Photos: boolean;
      [key: string]: boolean;
    };
    Hardware_box?: {
      [key: string]: boolean;
    };
    TV?: {
      ScreenSize: boolean;
      ScreenType: boolean;
      RemoteControl: boolean;
      PowerCord: boolean;
      TvBaseStand: boolean;
      BaseStandHardware: boolean;
    };
    Motorcycle?: {
      Manufacturer: boolean;
      Model: boolean;
      Year: boolean;
      SerialNumberVin: boolean;
      Color: boolean;
      OdometerMiles: boolean;
      Km: boolean;
      AccessoriesIncluded: boolean;
      GasTankEmpty: boolean;
      FuelSystemPurged: boolean;
      BatteryDisconnected: boolean;
    };
    [key: string]:
      | {
          [key: string]: boolean;
        }
      | undefined;
  };

  /**
   * Callback function when a category is toggled
   * @param categoryKey - The key of the category being toggled
   * @param value - The new boolean value
   */
  onCategoryToggle: (categoryKey: string, value: boolean) => void;

  /**
   * Callback function when a mandatory field is toggled
   * @param categoryKey - The key of the category containing the field
   * @param fieldKey - The key of the field being toggled
   * @param value - The new boolean value
   */
  onMandatoryFieldToggle: (categoryKey: string, fieldKey: string, value: boolean) => void;
}

export const ItemCategoryConfigDetails = ({
  isEnabled,
  categories,
  mandatoryFields,
  onCategoryToggle,
  onMandatoryFieldToggle,
}: ItemCategoryConfigDetailsProps) => {
  const { t: translate } = useTranslation();

  if (!isEnabled) return null;

  const hasItems =
    categories.Hardware_box ||
    categories.M_E ||
    categories.High_risk_High_value ||
    categories.Pro_gear ||
    categories.Vehicle;

  const tvMandatoryFields = mandatoryFields.TV ?? {
    ScreenSize: false,
    ScreenType: false,
    RemoteControl: false,
    PowerCord: false,
    TvBaseStand: false,
    BaseStandHardware: false,
  };

  const motorcycleMandatoryFields = mandatoryFields.Motorcycle ?? {
    Manufacturer: false,
    Model: false,
    Year: false,
    SerialNumberVin: false,
    Color: false,
    OdometerMiles: false,
    Km: false,
    AccessoriesIncluded: false,
    GasTankEmpty: false,
    FuelSystemPurged: false,
    BatteryDisconnected: false,
  };

  const renderItems = () => {
    if (!hasItems) return null;
    return (
      <>
        <Divider sx={{ marginLeft: "-16px", marginRight: "-16px" }} />

        <Box sx={{ paddingTop: "16px" }}>
          <MandatoryFieldDisplay>{translate("mandatoryFieldsByCategory")}</MandatoryFieldDisplay>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: "block" }}>
            {translate("checkedFieldsWillBeMarkedAsAMandatory")}
          </Typography>

          <Box display="flex" flexDirection="column" gap="16px" paddingTop="16px">
            {/* M&E Fields */}
            {categories.M_E && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MandatoryFieldTitle sx={{ flex: 2 }}>{translate("me")}</MandatoryFieldTitle>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: 3 }}>
                  <Checkbox
                    checked={mandatoryFields.M_E.Make}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Make", e.target.checked)}
                    label={translate("make")}
                  />
                  <Checkbox
                    checked={mandatoryFields.M_E.Model}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Model", e.target.checked)}
                    label={translate("model")}
                  />
                  <Checkbox
                    checked={mandatoryFields.M_E.Serial}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Serial", e.target.checked)}
                    label={translate("serial")}
                  />
                  <Checkbox
                    checked={mandatoryFields.M_E.Checklist}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Checklist", e.target.checked)}
                    label={translate("checklist")}
                  />
                  <Checkbox
                    checked={mandatoryFields.M_E.Comments}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Comments", e.target.checked)}
                    label={translate("comments")}
                  />
                  <Checkbox
                    checked={mandatoryFields.M_E.Photos}
                    onChange={(e) => onMandatoryFieldToggle("M_E", "Photos", e.target.checked)}
                    label={translate("photos")}
                  />
                </Box>
              </Box>
            )}

            {categories.M_E && <Divider />}

            {categories.M_E && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MandatoryFieldTitle sx={{ flex: 2 }}>{translate("tv")}</MandatoryFieldTitle>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: 3 }}>
                  <Checkbox
                    checked={tvMandatoryFields.ScreenSize}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "ScreenSize", e.target.checked)
                    }
                    label={translate("screenSize")}
                  />
                  <Checkbox
                    checked={tvMandatoryFields.ScreenType}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "ScreenType", e.target.checked)
                    }
                    label={translate("screenType")}
                  />
                  <Checkbox
                    checked={tvMandatoryFields.RemoteControl}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "RemoteControl", e.target.checked)
                    }
                    label={translate("remoteControl")}
                  />
                  <Checkbox
                    checked={tvMandatoryFields.PowerCord}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "PowerCord", e.target.checked)
                    }
                    label={translate("powerCord")}
                  />
                  <Checkbox
                    checked={tvMandatoryFields.TvBaseStand}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "TvBaseStand", e.target.checked)
                    }
                    label={translate("tvBaseStand")}
                  />
                  <Checkbox
                    checked={tvMandatoryFields.BaseStandHardware}
                    onChange={(e) =>
                      onMandatoryFieldToggle("TV", "BaseStandHardware", e.target.checked)
                    }
                    label={translate("baseStandHardware")}
                  />
                </Box>
              </Box>
            )}

            {categories.M_E && <Divider />}

            {categories.Vehicle && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MandatoryFieldTitle sx={{ flex: 2 }}>
                  {translate("motorcycle")}
                </MandatoryFieldTitle>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: 3 }}>
                  <Checkbox
                    checked={motorcycleMandatoryFields.Manufacturer}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "Manufacturer", e.target.checked)
                    }
                    label={translate("manufacturer")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.Model}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "Model", e.target.checked)
                    }
                    label={translate("model")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.Year}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "Year", e.target.checked)
                    }
                    label={translate("year")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.SerialNumberVin}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "SerialNumberVin", e.target.checked)
                    }
                    label={translate("serialNumberVin")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.Color}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "Color", e.target.checked)
                    }
                    label={translate("color")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.OdometerMiles}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "OdometerMiles", e.target.checked)
                    }
                    label={translate("odometerMiles")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.Km}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "Km", e.target.checked)
                    }
                    label={translate("km")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.AccessoriesIncluded}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "AccessoriesIncluded", e.target.checked)
                    }
                    label={translate("accessoriesIncluded")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.GasTankEmpty}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "GasTankEmpty", e.target.checked)
                    }
                    label={translate("gasTankEmpty")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.FuelSystemPurged}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "FuelSystemPurged", e.target.checked)
                    }
                    label={translate("fuelSystemPurged")}
                  />
                  <Checkbox
                    checked={motorcycleMandatoryFields.BatteryDisconnected}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Motorcycle", "BatteryDisconnected", e.target.checked)
                    }
                    label={translate("batteryDisconnected")}
                  />
                </Box>
              </Box>
            )}

            {categories.Vehicle && <Divider />}

            {/* Pro-gear Fields */}
            {categories.Pro_gear && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MandatoryFieldTitle sx={{ flex: 2 }}>Pro-gear</MandatoryFieldTitle>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: 3 }}>
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.Comments}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Pro_gear", "Comments", e.target.checked)
                    }
                    label={translate("comments")}
                  />
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.Photos}
                    onChange={(e) => onMandatoryFieldToggle("Pro_gear", "Photos", e.target.checked)}
                    label={translate("photos")}
                  />
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.Checklist}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Pro_gear", "Checklist", e.target.checked)
                    }
                    label={translate("checklist")}
                  />
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.Weight}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Pro_gear", "Weight", e.target.checked)
                    }
                    label={translate("weight")}
                  />
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.Volume}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Pro_gear", "Volume", e.target.checked)
                    }
                    label={translate("volume")}
                  />
                  <Checkbox
                    checked={mandatoryFields.Pro_gear.WeightOrVolume}
                    onChange={(e) =>
                      onMandatoryFieldToggle("Pro_gear", "WeightOrVolume", e.target.checked)
                    }
                    label={translate("weightOrVolume")}
                  />
                </Box>
              </Box>
            )}

            {categories.Pro_gear && <Divider />}

            {/* High-risk/High value Fields */}
            {categories.High_risk_High_value && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <MandatoryFieldTitle sx={{ flex: 2 }}>
                  {translate("highRiskHighValue")}
                </MandatoryFieldTitle>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: 3 }}>
                  <Checkbox
                    checked={mandatoryFields.High_risk_High_value.Make}
                    onChange={(e) =>
                      onMandatoryFieldToggle("High_risk_High_value", "Make", e.target.checked)
                    }
                    label={translate("make")}
                  />
                  <Checkbox
                    checked={mandatoryFields.High_risk_High_value.Model}
                    onChange={(e) =>
                      onMandatoryFieldToggle("High_risk_High_value", "Model", e.target.checked)
                    }
                    label={translate("model")}
                  />
                  <Checkbox
                    checked={mandatoryFields.High_risk_High_value.Serial}
                    onChange={(e) =>
                      onMandatoryFieldToggle("High_risk_High_value", "Serial", e.target.checked)
                    }
                    label={translate("serial")}
                  />
                  <Checkbox
                    checked={mandatoryFields.High_risk_High_value.Comments}
                    onChange={(e) =>
                      onMandatoryFieldToggle("High_risk_High_value", "Comments", e.target.checked)
                    }
                    label={translate("comments")}
                  />
                  <Checkbox
                    checked={mandatoryFields.High_risk_High_value.Photos}
                    onChange={(e) =>
                      onMandatoryFieldToggle("High_risk_High_value", "Photos", e.target.checked)
                    }
                    label={translate("photos")}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: hasItems ? "16px" : undefined }}
      >
        <MandatoryFieldTitle sx={{ flex: 2 }}>{translate("selectCategories")}</MandatoryFieldTitle>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, flex: 3 }}>
          <Checkbox
            checked={categories.M_E}
            onChange={(e) => onCategoryToggle("M_E", e.target.checked)}
            label={translate("me")}
          />
          <Checkbox
            checked={categories.Pro_gear}
            onChange={(e) => onCategoryToggle("Pro_gear", e.target.checked)}
            label="Pro-gear"
          />
          <Checkbox
            checked={categories.Hardware_box}
            onChange={(e) => onCategoryToggle("Hardware_box", e.target.checked)}
            label={translate("hardwareBox")}
          />
          <Checkbox
            checked={categories.High_risk_High_value}
            onChange={(e) => onCategoryToggle("High_risk_High_value", e.target.checked)}
            label={translate("highRiskHighValue")}
          />
          <Checkbox
            checked={categories.Vehicle}
            onChange={(e) => onCategoryToggle("Vehicle", e.target.checked)}
            label={translate("vehicle")}
          />
        </Box>
      </Box>

      {renderItems()}
    </Box>
  );
};
